<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <div>
        <section :class="$style.sectionRoot">
          <header :class="[$style.sectionHeader, $style.adBridgeSectionHeader]">
            Active Directory Integration
          </header>
          <div :class="$style.sectionContent">
            <p>
              Active Directory Integration agents import Users and Groups from AD to JumpCloud and
              syncs their attributes, passwords and group memberships. To get started, you'll need
              to install the JumpCloud agents on each of your AD Domains.
            </p>
            <p>
              For system requirements, recommended installation prerequisites, and to learn how
              to get your Active Directory Integration syncing as desired, see our two guides:
              <ExternalLink
                :href="KbArticleUrls.gettingStartedActiveDirectory"
                text="Getting Started: Active Directory and JumpCloud"
              />
              and
              <ExternalLink
                :href="KbArticleUrls.adiTroubleshooting"
                text="Troubleshooting ADI"
              />.
            </p>
          </div>
        </section>
        <section :class="$style.sectionRoot">
          <header :class="[$style.sectionHeader, $style.adBridgeSectionHeader]">
            AD Import
          </header>
          <div :class="$style.sectionContent">
            <p>
              Install the Import Agent on your domain to import and update JumpCloud with changes
              to User identities, attributes, passwords, and User groups from AD.
            </p>
            <FlatActionAnchor
              :href="adBridgeSetupUrl"
              text="Install a new Import Agent"
              @click="handleDownloadAdBridgeClick(importAgent)"
            />
          </div>
        </section>
        <section :class="$style.sectionRoot">
          <header :class="[$style.sectionHeader, $style.adBridgeSectionHeader]">
            AD Sync
          </header>
          <div :class="$style.sectionContent">
            <p>
              Install the Sync Agent as a supplement to the Import agent to allow
              <strong>password changes</strong> to sync from JumpCloud to AD for
              users on this domain.
            </p>
            <FlatActionAnchor
              defaultStyle
              :disabled="numberOfImportAgents === 0"
              :href="adBridgeSyncAgentUrl"
              text="Install a new Sync Agent"
              @click="handleDownloadAdBridgeClick(syncAgent)"
            />
          </div>
          <!-- @todo Commenting out until it is determined why this doesn't work for AD
          <DistributionGroupManagement
            :announcement="announcementInfo"
            :checkboxLabel="checkboxLabel"
            :parentType="parentType"
            :tooltipKbLink="tooltipKbLink"
            :tooltipText="tooltipText"
          />
          -->
        </section>
      </div>
    </template>
  </DetailsAsideLayout>
</template>
<script>
import * as Constants from 'client/util/Constants';
import { events, onboardingTasks } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapState } from 'vuex';

import ActiveDirectoryApi from 'client/api/v2/ActiveDirectoryApi';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
// @todo Commenting out until it is determined why this doesn't work
// import DistributionGroupManagement from
// 'client/directories/components/DistributionGroupManagement.vue';
import DownloadAdBridgeAgentModal from 'client/directories/ActiveDirectory/DownloadAdBridgeAgentModal.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import ExternalLink from 'client/components/ExternalLink.vue';
import FlatActionAnchor from 'client/components/Buttons/FlatActionAnchor.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'components/Modal/ModalEventBus';
import util from 'client/util/index';

const {
  AdBridgeAgentTypes,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  LocalStorageConsts,
  StoreModelMap,
  StoreModuleMap,
} = Constants;
const { triggerDirectoryAdded } = EventHelper;
const { triggerEvent } = EventManager;
const { activeDirectory, adBridgeAgent } = ItemTypeConsts;
const { error } = Constants.default;
const storeModel = StoreModelMap[activeDirectory];
const adBridgeAgentCollection = StoreModuleMap[adBridgeAgent];
const onboardingModule = 'OnboardingModule';

export default {
  name: 'AdBridgeDetails',

  components: {
    // DistributionGroupManagement,
    DetailsAsideLayout,
    ExternalLink,
    FlatActionAnchor,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  data: () => ({
    ...AdBridgeAgentTypes,
    KbArticleUrls,
  }),

  computed: {
    ...mapState('SettingsModule', [
      'adBridgeSetupUrl',
      'adBridgeSyncAgentUrl',
      'orgId',
    ]),

    ...mapState(storeModel, {
      domain: state => state.item.domain,
      groupsEnabled: state => state.item.groupsEnabled,
      id: state => state.item.id,
      numberOfImportAgents: state => state.item.numberOfImportAgents,
    }),

    announcementInfo() {
      return {
        enabled: false,
        header: 'Manage Groups in AD',
        intro: 'NEW',
        localStorageKey: LocalStorageConsts.adDistributionManagement,
        text: 'Now manage AD distribution groups and their membership entirely from within JumpCloud.',
      };
    },

    checkboxLabel() {
      return 'Enable management of groups and memberships in AD';
    },

    directoryName() {
      return ItemTypeDisplayNames[this.parentType];
    },

    tooltipKbLink() {
      return KbArticleUrls.adGroupManagement;
    },

    tooltipText() {
      return 'Enabling group management will allow you to manage groups and group memberships in AD.';
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    ...mapActions(adBridgeAgentCollection, [
      'fetchCollection',
      'resetCollection',
    ]),

    ...mapActions(onboardingModule, [
      'completeOnboardingTask',
    ]),

    displayDownloadAgentDialog(agentType) {
      if (agentType === this.syncAgent) {
        ActiveDirectoryApi
          .createAdBridgeSyncAgent(this.id)
          .then(data => this.openModal(agentType, data.connectKey))
          .catch(() => util.notify(error, 'Unable to get AD Integration Sync Agent connect key!'));
      } else {
        this.openModal(agentType);
      }
    },

    handleDownloadAdBridgeClick(agentType) {
      this.logOnboardingEvents();
      this.displayDownloadAgentDialog(agentType);

      if (agentType === this.syncAgent) {
        this.resetCollection({ soft: true });
        this.fetchCollection();
        triggerEvent(events.adDownloadSyncAgentInstallerClicked);
      } else {
        triggerEvent(events.adDownloadImportAgentInstallerClicked);
      }
    },

    /**
     * Log onboarding task event when the admin user downloads the AD Bridge
     * agent and launch the DownloadAdBridgeAgentModal displaying the required
     * v1 or v2 keys.
     */
    logOnboardingEvents() {
      triggerDirectoryAdded({ resourceName: 'ad-bridge' });
      this.completeOnboardingTask(onboardingTasks.discoverResources);
    },

    openModal(agentType, connectKey) {
      ModalEventBus.open({
        component: DownloadAdBridgeAgentModal,
        componentProps: {
          agentType,
          connectKey,
        },
      });
    },
  },
};
</script>
<style module>
@import '../../css/sections.css';

.sectionRoot > .adBridgeSectionHeader {
  position: static;
}

.domainNameContainer {
  width: 50%;
}
</style>
